import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { MODALS_ENUM } from "@constants/Modals";
import { DeleteMinerRequest, DeleteMinerResponse } from "@services/deleteMiner";
import {
  GetCurrentPoolConnection,
  IGetCurrentSettingsResponseRecord,
} from "@services/getSettings";
import useApiRequests from "@services/http/useApiRequest";
import { RootState } from "@store";
import { isMinerSelected } from "@store/minersSelect/actionTypes";
import { setDrag } from "@store/minersSelect/actions";
import { Tab } from "@store/minersSelect/actionTypes";
import { deleteMiner } from "@store/storedMiners/actions";
import { useAuth } from "@utils/hooks/useAuth";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import useMediaQuery from "@utils/hooks/utils";
import { makeNotifs } from "@utils/makeNotifs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { show } from "redux-modal";
import { useMinerContext } from "./MinerContext";
import { pub } from "@utils/pubsub";
import { IReproxyLinkEvent, ReproxyLinkRemoveEventTag, ReproxyPortListEvent, ReproxyPortListEventTag, ReproxyPortUpdateEvent, ReproxyPortUpdateEventTag } from "./minerReducer";
import useMinerPorts from "@services/miner/MinerPorts.hook";
import { useMe, useSettings } from "@utils/hooks/useMe";

export interface MinerRowsReorderData {
  dragged: string[];
  to: number;
}

export const useMinerRowControl = () => {
  const { formatMessage } = useIntl();
  const auth = useAuth();
  const me = useMe();
  //const settings = useSettings();
  const isDrag = useSelector(
    (state: RootState) => !!state.minersSelect.dragMinersIDs?.length
  );
  const dragged = useSelector(
    (root: RootState) => root.minersSelect.dragMinersIDs
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [noDrop, setNoDrop] = useState(false);
  const { miner, updateProxyLink } = useMinerContext();
  const { ok } = makeNotifs();
  const { onSuccess, onLoader } = useGlobalNotifs();
  const requests = useApiRequests();
  const dispatch = useDispatch();
  const isTablet = useMediaQuery("max", 1025);
  const isUnknownTab = useSelector(
    (state: RootState) => state.minersSelect.tab === Tab.UNKNOWN
  );
  const tab = useSelector((state: RootState) => state.minersSelect.tab);
  const [isLoadingConnection, setIsLoadingConnection] = useState(false);

  const onMouseUp = useRef(() => {
    dispatch(setDrag(tab));
    window.removeEventListener("mouseup", onMouseUp.current);
  });

  const onMouseUpOver = () => {
    if (!isDragOver || noDrop || !isDrag) return;
    // TODO: send reorder message
    pub("miner-rows-reorder", {
      dragged,
      to: miner.id,
    } as MinerRowsReorderData);
  };

  const onMouseEnter = () => {
    if (isDrag) {
      setIsDragOver(true);
      const noDrop = dragged?.includes(`${miner.id}`);
      //dbg.log("ENTER", dragged, miner.miner_id, noDrop);
      if (noDrop) setNoDrop(true);
    }
  };

  const onMouseLeave = () => {
    setIsDragOver(false);
    setNoDrop(false);
  };

  useEffect(() => {
    if (!isDrag) {
      setIsDragOver(false);
      setNoDrop(false);
    }
  }, [isDrag]);

  const selectedMiners = useSelector((state: RootState) => state.minersSelect);

  const onConnectHandler = useCallback(
    async (e?: React.MouseEvent) => {
      e?.stopPropagation();
      const res = await requests.enqueue<IGetCurrentSettingsResponseRecord>(
        new GetCurrentPoolConnection(String(miner.id)),
        { onLoadingStatusChanged: setIsLoadingConnection }
      );
      if (!res) return;
      dispatch(
        show(MODALS_ENUM.SHOW_PROXY_SETTING, {
          id: miner.uuid || miner.id,
          miner_id: miner.miner_id,
          // hash: miner.hash,
          settings: res.Record,
        })
      );
    },
    [miner]
  );

  const onDeleteHandler = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    dispatch(
      show(MODALS_ENUM.CONFIRM_MODAL, {
        title: formatMessage(
          { id: "modal.delete_miner.title" },
          { miner: miner.miner_id }
        ),
        subtitle: formatMessage({ id: "modal.delete_miner.sure" }),
        onConfirm: async () => {
          const response = await requests.enqueue<DeleteMinerResponse>(
            new DeleteMinerRequest(miner.id.toString(), isUnknownTab),
            {
              onLoadingStatusChanged: onLoader,
            }
          );
          if (!response) return;
          if (response.Records?.length) {
            dispatch(
              show(MODALS_ENUM.MINERS_DELETE_ERROR_MODAL, {
                miners: response.Records,
              })
            );
            return;
          }
          dispatch(deleteMiner("" + miner.id, tab));
          onSuccess(ok.success);
        },
      })
    );
  };

  const moreModal = () => {
    isTablet && dispatch(show(MODALS_ENUM.MORE_INFO_MODAL, { miner }));
  };

  const isMinerError = () => miner.status === MINER_STATUSES_E.FAIL; //CONNECTION_ERROR;

  const startDrag = () => {
    const selected = selectedMiners.selected.map((m) => m.id);
    if (selected.includes("" + miner.id)) {
      dispatch(setDrag(tab, selected));
    } else {
      dispatch(setDrag(tab, ["" + miner.id]));
    }
    window.addEventListener("mouseup", onMouseUp.current);
  };

  const [qrIsLoading, setQrLoading] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [minerQrImage, setMinerQrImage] = useState<string>("");

  const loadImage = async (id: number) => {
    if (showQr) {
      setShowQr(false);
      return;
    }
    setQrLoading(true);
    const data = await requests.getImage(`admin/miner/qrcode/${id}.png`);
    setMinerQrImage(URL.createObjectURL(data));
    setShowQr(true);
    setQrLoading(false);
  };

  const updateMinerPort = (
    port: number,
    prevPort: number,
    minerId: number,
  ) => {
    document.dispatchEvent(
      new CustomEvent(ReproxyPortUpdateEventTag, {
        detail: {
          minerId,
          port,
          prevPort,
        } as ReproxyPortUpdateEvent,
      })
    );
  }

  const minerPorts = useMinerPorts({
    onPortChanged: updateMinerPort
  });

  useEffect(() => {
    document.addEventListener(ReproxyLinkRemoveEventTag, onRemoveReproxyLink);
    return () => document.removeEventListener(ReproxyLinkRemoveEventTag, onRemoveReproxyLink);
    function onRemoveReproxyLink(event: Event) {
      const e = (event as CustomEvent).detail as IReproxyLinkEvent;
      if (miner.id === e.minerId) {
        updateProxyLink(0);
      }
    }
  }, [miner.id]);

  useEffect(() => {
    document.addEventListener(ReproxyPortUpdateEventTag, onUpdateReproxyPort);
    document.addEventListener(ReproxyPortListEventTag, onReproxyList);
    return () => {
      document.removeEventListener(ReproxyPortListEventTag, onReproxyList);
      document.removeEventListener(ReproxyPortUpdateEventTag, onUpdateReproxyPort);
    };
    function onUpdateReproxyPort(event: Event) {
      const e = (event as CustomEvent).detail as ReproxyPortUpdateEvent;
      if (miner.id === e.minerId /*&& miner.reproxy_link?.Port === e.prevPort*/) {
        updateProxyLink(e.port);
        return;
      }
      if (miner.reproxy_link?.Port === e.port && miner.id !== e.minerId) {
        updateProxyLink(0);
        return;
      }
    }
    function onReproxyList(event: Event) {
      const e = (event as CustomEvent).detail as ReproxyPortListEvent;
      const portEntry = e.ports.find(p => p.minerId === miner.id);
      if (portEntry) {
        updateProxyLink(portEntry.port);
      } else {
        updateProxyLink(0);
      }
    }

  }, [miner.id, miner.reproxy_link]);

  return {
    isChecked: isMinerSelected(selectedMiners, miner.id.toString()),
    isAdmin: auth.isAdmin,
    myEmail: me?.email.value,
    isDrag,
    miner,
    isLoading: isLoadingConnection,
    noDrop,
    isDragOver: isDragOver && !noDrop,

    loadImage,
    minerQrImage,
    showQr,
    setShowQr,
    qrIsLoading,
    // showCreatePortModal,
    isMinerError,
    onConnectHandler,
    onDeleteHandler,
    moreModal,
    startDrag,
    onMouseEnter,
    onMouseLeave,
    onMouseUpOver,
    //openMinerPort: minerPorts.openMinerPort,
    createPortAndOpen: minerPorts.createPortAndOpen,
    deleteMinerPort: minerPorts.deleteMinerPort,
  };
};
